import { useState } from "react";
import { useTranslations } from "use-intl";
import { toast } from "sonner";
import { clsx } from "clsx";
import { Button } from "@/components/catalyst/button";
import {
  Checkbox,
  CheckboxField,
  CheckboxGroup,
} from "@/components/catalyst/checkbox";
import { Field, Label } from "@/components/catalyst/fieldset";
import { Textarea } from "@/components/catalyst/textarea";
import {
  AlertActions,
  AlertBody,
  AlertDescription,
  AlertTitle,
} from "@/components/catalyst/alert";
import { AlertDialog } from "@/lib/Components/Dialog/AlertDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { rejectQuoteMutation } from "@/app/Quotes/GraphQL/quoteMutations";
import { QuoteDeclinedReason } from "@/gql/graphql";

const declineReasons = [
  QuoteDeclinedReason.PriceTooHigh,
  QuoteDeclinedReason.NoLongerNeeded,
  QuoteDeclinedReason.FoundAlternative,
  QuoteDeclinedReason.Other,
] as const;

export function DeclineQuoteDialog({ quoteId }: { quoteId: string }) {
  const t = useTranslations("quote.declineDialog");
  const { close, resolve, reject } = useDialogState();
  const { mutateAsync: declineQuote, isPending } =
    useGqlMutation(rejectQuoteMutation);
  const [selectedReason, setSelectedReason] =
    useState<QuoteDeclinedReason | null>(null);
  const [feedback, setFeedback] = useState("");
  const hasSelection = selectedReason !== null;

  return (
    <AlertDialog
      onClose={() => {
        reject?.(false);
      }}
    >
      <AlertTitle>{t("title")}</AlertTitle>
      <AlertDescription>{t("description")}</AlertDescription>
      <AlertBody>
        <p
          className={clsx(
            "mb-3 flex items-center gap-2 rounded-md px-3 py-2 text-sm",
            hasSelection
              ? "bg-green-50 text-green-700 dark:bg-green-400/10 dark:text-green-300"
              : "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300",
          )}
        >
          {hasSelection ? <span aria-hidden="true">✅</span> : null}
          {hasSelection ? t("reasonSelected") : t("reasonRequired")}
        </p>
        <CheckboxGroup>
          {declineReasons.map((reason) => (
            <CheckboxField
              key={reason}
              className="-mx-2 rounded-md px-2 py-1.5 transition-colors hover:bg-zinc-50 dark:hover:bg-white/5"
            >
              <Checkbox
                className="cursor-pointer"
                checked={selectedReason === reason}
                onChange={(checked) =>
                  setSelectedReason(checked ? reason : null)
                }
              />
              <Label className="-my-2 w-full cursor-pointer py-2">
                {t(`reasons.${reason}`)}
              </Label>
            </CheckboxField>
          ))}
        </CheckboxGroup>
        <Field className="mt-4">
          <Label>{t("feedbackLabel")}</Label>
          <Textarea
            rows={3}
            value={feedback}
            placeholder={t("feedbackPlaceholder")}
            onChange={(event) => setFeedback(event.target.value)}
          />
        </Field>
      </AlertBody>
      <AlertActions>
        <Button
          outline
          disabled={isPending}
          onClick={() => {
            close();
            reject?.(false);
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          color="red"
          className="data-disabled:opacity-40"
          isLoading={isPending}
          disabled={!hasSelection}
          onClick={async () => {
            if (!selectedReason) {
              return;
            }

            await declineQuote({
              id: quoteId,
              reason: selectedReason,
              description: feedback.trim() || undefined,
            });

            toast.success(t("quoteDeclined"));

            await close();
            resolve?.(true);
          }}
        >
          {t("confirm")}
        </Button>
      </AlertActions>
    </AlertDialog>
  );
}
